import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { DEFAULT_GAS_CHANNEL, EVENT_SOURCE } from '../../common/constants/analytics';
import type { SearchStoreExperience } from '../../common/constants/search';
import { SearchExperience } from '../../common/constants/search-experience';
import { type FireAnalyticsEvent, type FireAnalyticsScreenEvent } from '../../common/types';
import { useAppContext } from '../../common/ui/app-context';
import {
	useAnalyticsAttributes,
	useApplicationMode,
	useAvailableProducts,
	useExperimentConfig,
	usePrimaryProduct,
	useProductEdition,
	useSearchSessionId,
} from '../store/bootstrap';
import {
	useCQL,
	useFilterAttributesForTracking,
	useInputQuery,
	useQueryUpdatedTime,
	useResultsRequestId,
	useResultsSort,
	useSearchExperience,
	useSearchQuery,
	useSelectedProduct,
} from '../store/search';

import { getAttributes } from './utils';

type UseAnalyticsProps = {
	experience?: SearchStoreExperience;
};

/**
 * Hook to build out analytic events with shared properties
 *
 * Accepts an optional experience: SearchStoreExperience parameter to denote the search experience context, to be used if the search store wont be ready, e.g. during bootstrap phase
 */
export const useAnalytics: (props?: UseAnalyticsProps) => {
	fireAnalyticsEvent: (payload: FireAnalyticsEvent) => void;
	fireAnalyticsScreenEvent: (payload: FireAnalyticsScreenEvent) => void;
} = (props: UseAnalyticsProps = {}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// analytics attributes passed in from the product
	const [analyticsAttributes] = useAnalyticsAttributes();
	const [searchExperience] = useSearchExperience();

	const experiencePayload = useMemo(() => {
		// determine experience payload for analytics attributes, defaulting to store value if not provided
		const experience = props.experience || searchExperience;
		const isQuickFind = experience === SearchExperience.QUICK_FIND;
		const isFullPageSearch = experience === SearchExperience.SEARCH_PAGE;
		return {
			isFullPageSearch,
			isQuickFind,
		};
	}, [props.experience, searchExperience]);

	// use this hook to fire UI, track, and operational event types
	const fireAnalyticsEvent = useCallback(
		(payload: FireAnalyticsEvent): void => {
			createAnalyticsEvent({
				...payload,
				source: payload.source || EVENT_SOURCE,
				attributes: {
					...payload.attributes.commonAttributes,
					...payload.attributes.additionalAttributes,
					...analyticsAttributes,
					...experiencePayload,
				},
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[analyticsAttributes, createAnalyticsEvent, experiencePayload],
	);

	// use this hook to fire screen event types
	const fireAnalyticsScreenEvent = useCallback(
		(payload: FireAnalyticsScreenEvent): void => {
			createAnalyticsEvent({
				...payload,
				eventType: 'screen',
				source: EVENT_SOURCE,
				attributes: {
					...payload.attributes.commonAttributes,
					...payload.attributes.additionalAttributes,
					...analyticsAttributes,
					...experiencePayload,
				},
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[analyticsAttributes, createAnalyticsEvent, experiencePayload],
	);

	return { fireAnalyticsEvent, fireAnalyticsScreenEvent };
};

export const useCommonAndPrivateAttributes: () => {
	commonAttributes: import('../../common/utils/events/types').CommonAttributes;
	nonPrivacySafeAttributes: {
		query: string;
		inputQuery: string;
	};
} = () => {
	const [searchSessionId] = useSearchSessionId();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [requestId] = useResultsRequestId();
	const [searchQuery] = useSearchQuery();
	const [inputQuery] = useInputQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryUpdatedTime] = useQueryUpdatedTime();
	const [productSelected] = useSelectedProduct();
	const availableProducts = useAvailableProducts();
	const [sort] = useResultsSort();
	const [experimentConfig] = useExperimentConfig();
	const filters = useFilterAttributesForTracking();
	const applicationMode = useApplicationMode();
	const [productEdition] = useProductEdition();
	const cqlState = useCQL();
	const [primaryProduct] = usePrimaryProduct();
	const { queryParams } = useAppContext();

	return getAttributes({
		searchSessionId,
		requestId,
		searchQuery,
		inputQuery,
		filters,
		queryUpdatedTime,
		productSelected,
		availableProducts,
		sort,
		experimentConfig,
		applicationMode,
		productEdition,
		isCQL: !!cqlState,
		primaryProduct,
		queryParams,
	});
};
